"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Management() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    title: "Precision Diagnostics",
    body: "Advanced analytical tools to identify system inefficiencies and optimize performance with pinpoint accuracy.",
    icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10" aria-hidden="true">
          <rect x="9" y="20" width="30" height="9" rx="4.5" />
          <line x1="6" y1="24.5" x2="9" y2="24.5" />
          <line x1="39" y1="24.5" x2="42" y2="24.5" />
          <line x1="4" y1="22.5" x2="4" y2="26.5" />
          <line x1="44" y1="22.5" x2="44" y2="26.5" />
          <line x1="15" y1="20" x2="15" y2="29" />
          <line x1="33" y1="20" x2="33" y2="29" />
        </svg>
  }, {
    title: "Operational Efficiency",
    body: "Streamlined workflows designed to reduce downtime and maximize the output of your industrial infrastructure.",
    icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10" aria-hidden="true">
          <path d="M8 18 L40 18 L36 38 L12 38 Z" />
          <line x1="14" y1="18" x2="17" y2="38" />
          <line x1="24" y1="18" x2="24" y2="38" />
          <line x1="34" y1="18" x2="31" y2="38" />
          <line x1="10" y1="26" x2="38" y2="26" />
          <path d="M18 18 C18 12, 22 9, 24 9 C26 9, 30 12, 30 18" />
        </svg>
  }, {
    title: "System Integration",
    body: "Seamless connectivity between legacy hardware and modern control systems for unified facility management.",
    icon: <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10" aria-hidden="true">
          <path d="M8 40 L8 16 L24 6 L40 16 L40 40 Z" />
          <path d="M16 40 L16 26 L32 26 L32 40" />
          <path d="M24 34 C22 32, 22 30, 24 28 C26 30, 26 32, 24 34 Z" />
          <path d="M21 24 C20 22, 21 20, 23 19" />
          <path d="M27 24 C28 22, 27 20, 25 19" />
        </svg>
  }];
  return <section data-section-id="11910" id="management-features" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div ref={intro.ref} className={`max-w-2xl mx-auto text-center transition-all duration-[620ms] ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-lg md:text-xl mb-4" style={{
          color: "var(--primary)"
        }}>
            engineered for excellence
          </p>
          <h2 className="font-default font-normal text-foreground text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.08] tracking-tight">
            Advanced Management{" "}
            <span className="relative inline-block italic">
              solutions
              <svg viewBox="0 0 220 16" preserveAspectRatio="none" className="absolute left-0 -bottom-2 w-full h-3 overflow-visible" aria-hidden="true">
                <path d="M4 10 C 40 4, 90 14, 130 7 C 165 2, 200 10, 216 6" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" style={{
                strokeDasharray: 260,
                strokeDashoffset: intro.isVisible ? 0 : 260,
                transition: "stroke-dashoffset 540ms ease-out 200ms"
              }} />
              </svg>
            </span>
          </h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
            Comprehensive tools to maintain peak industrial performance.
          </p>
        </div>

        <div ref={grid.ref} className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {features.map((feature, i) => <div data-index={i} key={feature.title} className={`group relative bg-card text-card-foreground border p-7 transition-all duration-[620ms] ease-out hover:-translate-y-1 ${i === 1 ? "md:rotate-[0.5deg]" : ""} ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          borderColor: "var(--border)",
          borderRadius: "18px 8px 18px 18px",
          transitionDelay: `${i * 80}ms`,
          boxShadow: "var(--shadow-sm)"
        }} onMouseEnter={e => {
          e.currentTarget.style.boxShadow = "0 18px 40px -20px hsl(178 15% 40% / 0.18)";
        }} onMouseLeave={e => {
          e.currentTarget.style.boxShadow = "var(--shadow-sm)";
        }}>
              <div className="mb-6 transition-transform duration-500 ease-out group-hover:rotate-[-2deg]" style={{
            color: "var(--primary)"
          }}>
                {feature.icon}
              </div>
              <h3 className="font-default font-medium text-foreground text-[clamp(1.375rem,2.2vw,1.875rem)] leading-[1.2] tracking-tight mb-3">
                {feature.title}
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed">
                {feature.body}
              </p>

              {i === 1 && <svg viewBox="0 0 60 60" className="absolute -top-3 -right-3 w-10 h-10 opacity-70" aria-hidden="true">
                  <path d="M10 30 C 14 18, 30 12, 48 16" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
                  <path d="M44 12 L48 16 L44 22" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>}
            </div>)}
        </div>
      </div>
    </section>;
}